
// Calculate trend signal based on RSI, MACD histogram, and Stochastic %K/%D
export function calculateTrend(rsi, macdHistogram, stochK, stochD) {
  // Handle null/undefined values
  if (rsi === null || rsi === undefined ||
      macdHistogram === null || macdHistogram === undefined ||
      stochK === null || stochK === undefined ||
      stochD === null || stochD === undefined) {
    return 'Sideways';
  }

  // Count bullish and bearish signals
  let bullishSignals = 0;
  let bearishSignals = 0;

  // RSI signal (> 50 bullish, < 50 bearish)
  if (rsi > 50) {
    bullishSignals++;
  } else {
    bearishSignals++;
  }

  // MACD histogram signal (> 0 bullish, < 0 bearish)
  if (macdHistogram > 0) {
    bullishSignals++;
  } else {
    bearishSignals++;
  }

  // Stochastic signal (%K > %D bullish, %K < %D bearish)
  if (stochK > stochD) {
    bullishSignals++;
  } else {
    bearishSignals++;
  }

  // Determine overall trend based on majority signals
  if (bullishSignals >= 2) {
    return 'Long';
  } else if (bearishSignals >= 2) {
    return 'Short';
  } else {
    return 'Sideways';
  }
}

// Calculate trend signal with additional strength indication
export function calculateTrendWithStrength(rsi, macdHistogram, stochK, stochD) {
  const trend = calculateTrend(rsi, macdHistogram, stochK, stochD);
  
  // Handle null values
  if (rsi === null || macdHistogram === null || stochK === null || stochD === null) {
    return {
      direction: 'Sideways',
      strength: 'Weak'
    };
  }

  // Calculate strength based on how extreme the values are
  let strengthScore = 0;

  // RSI strength (more extreme = stronger)
  if (rsi > 70 || rsi < 30) {
    strengthScore += 2;
  } else if (rsi > 60 || rsi < 40) {
    strengthScore += 1;
  }

  // MACD histogram strength (larger absolute value = stronger)
  const macdAbs = Math.abs(macdHistogram);
  if (macdAbs > 0.1) {
    strengthScore += 2;
  } else if (macdAbs > 0.05) {
    strengthScore += 1;
  }

  // Stochastic strength (more extreme = stronger)
  const stochDiff = Math.abs(stochK - stochD);
  if (stochDiff > 20) {
    strengthScore += 2;
  } else if (stochDiff > 10) {
    strengthScore += 1;
  }

  // Determine strength level
  let strength;
  if (strengthScore >= 4) {
    strength = 'Strong';
  } else if (strengthScore >= 2) {
    strength = 'Medium';
  } else {
    strength = 'Weak';
  }

  return {
    direction: trend,
    strength: strength
  };
}

// Calculate trend for multiple timeframes
export function calculateMultiTimeframeTrend(indicators) {
  const { weekly, daily, fourHour } = indicators;
  
  const trends = {};

  // Calculate trend for each timeframe
  if (weekly.rsi && weekly.macd && weekly.stochastic) {
    const weeklyRSI = weekly.rsi[weekly.rsi.length - 1];
    const weeklyMACD = weekly.macd.histogram[weekly.macd.histogram.length - 1];
    const weeklyStochK = weekly.stochastic.k[weekly.stochastic.k.length - 1];
    const weeklyStochD = weekly.stochastic.d[weekly.stochastic.d.length - 1];
    
    trends.weekly = calculateTrend(weeklyRSI, weeklyMACD, weeklyStochK, weeklyStochD);
  } else {
    trends.weekly = 'N/A';
  }

  if (daily.rsi && daily.macd && daily.stochastic) {
    const dailyRSI = daily.rsi[daily.rsi.length - 1];
    const dailyMACD = daily.macd.histogram[daily.macd.histogram.length - 1];
    const dailyStochK = daily.stochastic.k[daily.stochastic.k.length - 1];
    const dailyStochD = daily.stochastic.d[daily.stochastic.d.length - 1];
    
    trends.daily = calculateTrend(dailyRSI, dailyMACD, dailyStochK, dailyStochD);
  } else {
    trends.daily = 'N/A';
  }

  if (fourHour.rsi && fourHour.macd && fourHour.stochastic) {
    const fourHourRSI = fourHour.rsi[fourHour.rsi.length - 1];
    const fourHourMACD = fourHour.macd.histogram[fourHour.macd.histogram.length - 1];
    const fourHourStochK = fourHour.stochastic.k[fourHour.stochastic.k.length - 1];
    const fourHourStochD = fourHour.stochastic.d[fourHour.stochastic.d.length - 1];
    
    trends.fourHour = calculateTrend(fourHourRSI, fourHourMACD, fourHourStochK, fourHourStochD);
  } else {
    trends.fourHour = 'N/A';
  }

  return trends;
}

// Calculate overall market sentiment based on multiple coins
export function calculateMarketSentiment(coinTrends) {
  if (!coinTrends || coinTrends.length === 0) {
    return {
      overall: 'Neutral',
      bullishCount: 0,
      bearishCount: 0,
      sidewaysCount: 0
    };
  }

  let bullishCount = 0;
  let bearishCount = 0;
  let sidewaysCount = 0;

  coinTrends.forEach(trend => {
    if (trend === 'Long') {
      bullishCount++;
    } else if (trend === 'Short') {
      bearishCount++;
    } else {
      sidewaysCount++;
    }
  });

  const totalCount = coinTrends.length;
  const bullishPercent = (bullishCount / totalCount) * 100;
  const bearishPercent = (bearishCount / totalCount) * 100;

  let overall;
  if (bullishPercent > 60) {
    overall = 'Very Bullish';
  } else if (bullishPercent > 45) {
    overall = 'Bullish';
  } else if (bearishPercent > 60) {
    overall = 'Very Bearish';
  } else if (bearishPercent > 45) {
    overall = 'Bearish';
  } else {
    overall = 'Neutral';
  }

  return {
    overall,
    bullishCount,
    bearishCount,
    sidewaysCount,
    bullishPercent: bullishPercent.toFixed(1),
    bearishPercent: bearishPercent.toFixed(1),
    sidewaysPercent: ((sidewaysCount / totalCount) * 100).toFixed(1)
  };
}
